import { api, ApiError } from "../../services/apiClient";
import { normalizePhone } from "../../services/phoneNumber";

// WhatsApp (WAHA) settings (Admin → Settings). The server re-validates everything, stores the
// whole configuration encrypted, and never returns the API key (only hasApiKey). These helpers
// give immediate feedback and build request payloads; the key lives only in component state
// while typing and is never stored in the browser.

export interface WahaSettingsView {
  enabled: boolean;
  /** Enabled AND complete: new sign-in codes and invitations are sent through WhatsApp. */
  deliveryActive: boolean;
  configured: boolean;
  complete: boolean;
  baseUrl: string | null;
  session: string;
  testRecipient: string | null;
  testRecipientDisplay: string | null;
  hasApiKey: boolean;
  updatedAt: string | null;
}

export interface WahaSettingsForm {
  enabled: boolean;
  baseUrl: string;
  session: string;
  /** Write-only: blank keeps the saved key. */
  apiKey: string;
  clearApiKey: boolean;
  testRecipient: string;
}

export interface WahaSettingsPayload {
  enabled: boolean;
  baseUrl: string;
  session: string;
  testRecipient: string;
  apiKey?: string;
  clearApiKey?: true;
}

export type WahaCheckResult =
  | "working" | "session-not-working" | "unauthorized" | "session-not-found"
  | "unreachable" | "timeout" | "blocked-target" | "failed";

export type WahaTestResult = "accepted" | "rejected" | "unknown";

export type WahaErrorKey =
  | "urlInvalid" | "urlTarget" | "sessionInvalid" | "apiKeyInvalid" | "recipientInvalid"
  | "clearConflict" | "enableIncomplete" | "disableFirst" | "incomplete" | "recipientRequired"
  | "tooMany" | "tooManyTests" | "httpsRequired" | "unavailable" | "generic";

const SESSION = /^[A-Za-z0-9_-]{1,64}$/;
const BLOCKED_HOSTS = /^(localhost|.*\.localhost|127\.\d+\.\d+\.\d+|0\.0\.0\.0|169\.254\.\d+\.\d+|\[::1?\])$/i;

export function formFromView(v: WahaSettingsView): WahaSettingsForm {
  return {
    enabled: v.enabled,
    baseUrl: v.baseUrl ?? "",
    session: v.session || "default",
    apiKey: "",
    clearApiKey: false,
    testRecipient: v.testRecipientDisplay ?? v.testRecipient ?? ""
  };
}

/** Client mirror of the server rules (the server stays authoritative). */
export function validateWahaForm(f: WahaSettingsForm, hasSavedKey: boolean): WahaErrorKey | null {
  const url = f.baseUrl.trim();
  if (url) {
    if (url.length > 512 || /[\s?#%\\@]/.test(url) || !/^https:\/\/[^/]+/i.test(url)) return "urlInvalid";
    let host: string;
    try {
      host = new URL(url).host.replace(/:\d+$/, "");
    } catch {
      return "urlInvalid";
    }
    if (BLOCKED_HOSTS.test(host)) return "urlTarget";
  }
  const session = f.session.trim() || "default";
  if (!SESSION.test(session)) return "sessionInvalid";
  if (f.apiKey && f.clearApiKey) return "clearConflict";
  if (f.apiKey && !isHeaderSafeKey(f.apiKey)) return "apiKeyInvalid";
  if (f.testRecipient.trim() && !normalizePhone(f.testRecipient)) return "recipientInvalid";
  if (f.enabled) {
    if (f.clearApiKey) return "disableFirst";
    const willHaveKey = f.apiKey !== "" || hasSavedKey;
    if (!url || !willHaveKey) return "enableIncomplete";
  }
  return null;
}

export function isHeaderSafeKey(key: string): boolean {
  return key.length > 0 && key.length <= 512 && /^[\x21-\x7e]([\x20-\x7e]*[\x21-\x7e])?$/.test(key);
}

/** Request body: explicit enabled flag; a blank key is omitted (keeps the saved key). */
export function buildWahaPayload(f: WahaSettingsForm): WahaSettingsPayload {
  const payload: WahaSettingsPayload = {
    enabled: f.enabled,
    baseUrl: f.baseUrl.trim(),
    session: f.session.trim() || "default",
    testRecipient: f.testRecipient.trim()
  };
  if (f.clearApiKey) payload.clearApiKey = true;
  else if (f.apiKey) payload.apiKey = f.apiKey;
  return payload;
}

/** Check/test actions use the SAVED configuration; unsaved form edits would be misleading. */
export function isFormDirty(f: WahaSettingsForm, v: WahaSettingsView): boolean {
  const saved = formFromView(v);
  return f.enabled !== saved.enabled || f.baseUrl.trim() !== saved.baseUrl || (f.session.trim() || "default") !== saved.session
    || f.apiKey !== "" || f.clearApiKey || f.testRecipient.trim() !== saved.testRecipient;
}

export function wahaErrorKey(err: unknown): WahaErrorKey {
  if (err instanceof ApiError) {
    switch (err.problem.type) {
      case "VC-WAHA-001": return "urlInvalid";
      case "VC-WAHA-002": return "urlTarget";
      case "VC-WAHA-003": return "sessionInvalid";
      case "VC-WAHA-004": return "apiKeyInvalid";
      case "VC-WAHA-005": return "recipientInvalid";
      case "VC-WAHA-006": return "clearConflict";
      case "VC-WAHA-008": return "enableIncomplete";
      case "VC-WAHA-009": return "disableFirst";
      case "VC-WAHA-010": return "incomplete";
      case "VC-WAHA-011": return "recipientRequired";
      case "VC-WAHA-403": return "httpsRequired";
      case "VC-WAHA-429": return "tooMany";
      case "VC-WAHA-430": return "tooManyTests";
      case "VC-WAHA-503": return "unavailable";
    }
    if (err.status === 503) return "unavailable";
  }
  return "generic";
}

const PATH = "/admin/settings/waha";

export const getWahaSettings = (token: string) => api.get<WahaSettingsView>(PATH, token);
export const saveWahaSettings = (token: string, payload: WahaSettingsPayload) => api.put<WahaSettingsView>(PATH, payload, token);
export const checkWahaConnection = (token: string) => api.post<{ result: WahaCheckResult }>(`${PATH}/check`, undefined, token);
/** Always `{}`: the server sends only to the SAVED test recipient (no client-side target). */
export const sendWahaTestMessage = (token: string) =>
  api.post<{ result: WahaTestResult }>(`${PATH}/test-message`, {}, token);
